/* =============================================================================
   Legere Parts — núcleo do tema
   Utilitários compartilhados e comportamentos de interface que não dependem
   do carrinho. Sem framework: eventos delegados + atributos data-*.
   ========================================================================== */
(function () {
  'use strict';

  const T = (window.LegereTheme = window.LegereTheme || {});
  T.strings = T.strings || {};

  /* --- Utilitários --------------------------------------------------------- */

  /** Formata centavos usando o money_format da loja. */
  function formatMoney(cents) {
    const value = (Number(cents) / 100).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    const format = T.moneyFormat || 'R$ {{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /** GET/POST JSON com erro legível. */
  async function fetchJSON(url, options) {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...options,
    });
    const data = await res.json().catch(() => null);
    if (!res.ok) {
      const message = (data && (data.description || data.message)) || `HTTP ${res.status}`;
      throw new Error(message);
    }
    return data;
  }

  function debounce(fn, wait) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  /** Mensagem temporária no canto da tela. */
  function toast(message, variant) {
    const region = document.getElementById('ToastRegion');
    if (!region) return;
    const el = document.createElement('div');
    el.className = 'toast' + (variant ? ` toast--${variant}` : '');
    el.textContent = message;
    region.appendChild(el);
    void el.offsetWidth; // ver a nota sobre reflow em cart.js
    el.classList.add('is-visible');
    setTimeout(() => {
      el.classList.remove('is-visible');
      el.addEventListener('transitionend', () => el.remove(), { once: true });
      setTimeout(() => el.remove(), 600);
    }, 3200);
  }

  /* --- Foco preso em modais ------------------------------------------------ */

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  let trapState = null;

  function trapFocus(container, onEscape) {
    releaseFocus();
    const previous = document.activeElement;

    function onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (onEscape) onEscape();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = Array.from(container.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.offsetParent !== null
      );
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeydown);
    trapState = { onKeydown, previous };

    // preventScroll: o botão de fechar do drawer/modal já está visível (é
    // position: fixed), então sem isso o navegador tenta "trazer o elemento
    // pra vista" rolando a página — some por trás do cabeçalho fixo, que
    // gruda a partir do scroll do <html>, e o cabeçalho salta pra uma
    // posição errada até a rolagem assentar.
    const target = container.querySelector('[data-autofocus]') || container.querySelector(FOCUSABLE);
    if (target) target.focus({ preventScroll: true });
    document.documentElement.classList.add('is-locked');
  }

  function releaseFocus() {
    if (!trapState) return;
    document.removeEventListener('keydown', trapState.onKeydown);
    if (trapState.previous && trapState.previous.focus) trapState.previous.focus();
    trapState = null;
    document.documentElement.classList.remove('is-locked');
  }

  /* --- Listas suspensas (<details>) ---------------------------------------- */

  // Fecha os menus abertos ao clicar fora ou ao pressionar Escape.
  document.addEventListener('click', (event) => {
    document.querySelectorAll('details[data-dropdown][open]').forEach((el) => {
      if (!el.contains(event.target)) el.removeAttribute('open');
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-dropdown][open]').forEach((el) => {
      el.removeAttribute('open');
      const summary = el.querySelector('summary');
      if (summary) summary.focus();
    });
  });

  /* --- Carrosséis ---------------------------------------------------------- */

  function initCarousel(root) {
    // As setas de navegação ficam no cabeçalho da seção (.section__head),
    // fora de [data-carousel] — em toda seção com carrossel do tema, não só
    // aqui. Por isso a busca sobe até a .section inteira, não só a "root".
    const scope = root.closest('.section') || root;
    const track = root.querySelector('[data-carousel-track]');
    const prev = scope.querySelector('[data-carousel-prev]');
    const next = scope.querySelector('[data-carousel-next]');
    if (!track) return;

    function step() {
      const first = track.firstElementChild;
      if (!first) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap || '20') || 20;
      return first.getBoundingClientRect().width + gap;
    }

    function sync() {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
    }

    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
    track.addEventListener('scroll', debounce(sync, 80), { passive: true });
    window.addEventListener('resize', debounce(sync, 150));
    sync();
  }

  /* --- Slider do hero ------------------------------------------------------ */

  function initHeroSlider(root) {
    const slides = Array.from(root.querySelectorAll('[data-hero-slide]'));
    const dots = Array.from(root.querySelectorAll('[data-hero-dot]'));
    const prevBtn = root.querySelector('[data-hero-prev]');
    const nextBtn = root.querySelector('[data-hero-next]');
    if (slides.length < 2) return;

    const interval = Number(root.dataset.autoplay || 0) * 1000;
    let index = 0;
    let timer = null;

    function show(next) {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => {
        const active = i === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', String(i === index));
      });
    }

    function start() {
      if (!interval) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      stop();
      timer = setInterval(() => show(index + 1), interval);
    }
    function stop() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    dots.forEach((dot, i) =>
      dot.addEventListener('click', () => {
        show(i);
        start();
      })
    );

    if (prevBtn) prevBtn.addEventListener('click', () => { show(index - 1); start(); });
    if (nextBtn) nextBtn.addEventListener('click', () => { show(index + 1); start(); });

    root.addEventListener('mouseenter', stop);
    root.addEventListener('mouseleave', start);
    root.addEventListener('focusin', stop);

    show(0);
    start();
  }

  /* --- Abas ---------------------------------------------------------------- */

  function initTabs(root) {
    const tabs = Array.from(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function activate(tab) {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.setAttribute('aria-selected', String(selected));
        t.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    }

    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (tab) activate(tab);
    });

    root.addEventListener('keydown', (event) => {
      const current = tabs.indexOf(document.activeElement);
      if (current < 0) return;
      let next = null;
      if (event.key === 'ArrowRight') next = tabs[(current + 1) % tabs.length];
      if (event.key === 'ArrowLeft') next = tabs[(current - 1 + tabs.length) % tabs.length];
      if (event.key === 'Home') next = tabs[0];
      if (event.key === 'End') next = tabs[tabs.length - 1];
      if (!next) return;
      event.preventDefault();
      next.focus();
      activate(next);
    });
  }

  /* --- Contagem regressiva ------------------------------------------------- */

  function initCountdown(root) {
    const target = new Date(root.dataset.countdown).getTime();
    if (Number.isNaN(target)) return;

    const parts = {
      days: root.querySelector('[data-countdown-days]'),
      hours: root.querySelector('[data-countdown-hours]'),
      minutes: root.querySelector('[data-countdown-minutes]'),
      seconds: root.querySelector('[data-countdown-seconds]'),
    };

    function pad(n) {
      return String(Math.max(0, n)).padStart(2, '0');
    }

    let timer = null;

    function tick() {
      const diff = target - Date.now();
      if (diff <= 0) {
        root.classList.add('is-ended');
        Object.values(parts).forEach((el) => el && (el.textContent = '00'));
        clearInterval(timer);
        return;
      }
      const s = Math.floor(diff / 1000);
      if (parts.days) parts.days.textContent = pad(Math.floor(s / 86400));
      if (parts.hours) parts.hours.textContent = pad(Math.floor((s % 86400) / 3600));
      if (parts.minutes) parts.minutes.textContent = pad(Math.floor((s % 3600) / 60));
      if (parts.seconds) parts.seconds.textContent = pad(s % 60);
    }

    timer = setInterval(tick, 1000);
    tick();
  }

  /* --- Galeria do produto -------------------------------------------------- */

  function initGallery(root) {
    const main = root.querySelector('[data-gallery-main]');
    const thumbs = Array.from(root.querySelectorAll('[data-gallery-thumb]'));
    if (!main || !thumbs.length) return;

    function select(thumb) {
      const src = thumb.dataset.imageSrc;
      const srcset = thumb.dataset.imageSrcset || '';
      if (src) {
        main.src = src;
        main.srcset = srcset;
        main.alt = thumb.dataset.imageAlt || main.alt;
        main.dataset.zoomSrc = thumb.dataset.zoomSrc || src;
      }
      thumbs.forEach((t) => {
        t.classList.toggle('is-active', t === thumb);
        t.setAttribute('aria-current', String(t === thumb));
      });
    }

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => select(thumb)));
    select(thumbs.find((t) => t.classList.contains('is-active')) || thumbs[0]);
  }

  /* --- Zoom da imagem ------------------------------------------------------ */

  function initImageZoom() {
    const overlay = document.querySelector('[data-image-zoom]');
    if (!overlay) return;
    const img = overlay.querySelector('[data-image-zoom-img]');

    function open(src, alt) {
      img.src = src;
      img.alt = alt || '';
      overlay.hidden = false;
      void overlay.offsetWidth; // ver a nota sobre reflow em cart.js
      overlay.classList.add('is-open');
      trapFocus(overlay, close);
    }

    function close() {
      overlay.classList.remove('is-open');
      releaseFocus();
      setTimeout(() => {
        overlay.hidden = true;
        img.removeAttribute('src');
      }, 200);
    }

    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-zoom-trigger]');
      if (trigger) {
        event.preventDefault();
        const source = trigger.querySelector('img') || trigger;
        open(source.dataset.zoomSrc || source.src, source.alt);
        return;
      }
      if (event.target.closest('[data-image-zoom-close]') || event.target === overlay) close();
    });
  }

  /* --- Modais de conta (perfil, endereços) ---------------------------------- */

  /**
   * Genérico: qualquer número de modais na página, cada um com [data-modal]
   * e um id próprio, abertos por um gatilho [data-modal-open="id-do-modal"]
   * em qualquer lugar da página (várias abas de endereço, por exemplo).
   */
  function initAccountModals() {
    const modals = document.querySelectorAll('[data-modal]');
    if (!modals.length) return;

    function open(modal) {
      modal.hidden = false;
      void modal.offsetWidth; // ver a nota sobre reflow em cart.js
      modal.classList.add('is-open');
      trapFocus(modal, () => close(modal));
    }

    function close(modal) {
      modal.classList.remove('is-open');
      releaseFocus();
      setTimeout(() => {
        modal.hidden = true;
      }, 200);
    }

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-modal-open]');
      if (opener) {
        const modal = document.getElementById(opener.dataset.modalOpen);
        if (modal) {
          event.preventDefault();
          open(modal);
        }
        return;
      }
      const closer = event.target.closest('[data-modal-close]');
      if (closer) {
        event.preventDefault();
        close(closer.closest('[data-modal]'));
      }
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('[data-confirm]');
      if (form && !window.confirm(form.dataset.confirm)) event.preventDefault();
    });
  }

  /* --- Gaveta de categorias (mobile) ---------------------------------------- */

  function initDeptDrawer() {
    const el = document.querySelector('[data-dept-drawer]');
    if (!el) return;

    function open() {
      el.hidden = false;
      void el.offsetWidth; // ver a nota sobre reflow em cart.js
      el.classList.add('is-open');
      trapFocus(el, close);
    }

    function close() {
      el.classList.remove('is-open');
      releaseFocus();
      setTimeout(() => {
        el.hidden = true;
      }, 250);
    }

    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-dept-drawer-open]')) {
        event.preventDefault();
        open();
        return;
      }
      if (event.target.closest('[data-dept-drawer-close]')) close();
    });
  }

  /* --- Acordeão automático (páginas de conteúdo) ---------------------------- */

  const ACCORDION_CHEVRON =
    '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /**
   * Transforma cada <h2> do texto — e tudo que vem depois dele até o
   * próximo <h2> — num <details class="accordion">, igual à seção de FAQ.
   * Deixa quem escreveu a página de FAQ direto no campo Conteúdo (em vez de
   * recriar cada pergunta como bloco) ganhar o mesmo acordeão clicável.
   */
  function initAutoAccordion(root) {
    root.querySelectorAll('[data-auto-accordion]').forEach((container) => {
      if (container.dataset.accordionReady) return;
      container.dataset.accordionReady = 'true';

      // Sem ":scope >": o texto colado no editor costuma vir com um <div>
      // embrulhando tudo (herdado do editor de origem), então os <h2> quase
      // nunca são filhos diretos de [data-auto-accordion] — só precisam ser
      // irmãos entre si e do conteúdo que os segue, não importa a profundidade.
      Array.from(container.querySelectorAll('h2')).forEach((heading) => {
        const details = document.createElement('details');
        details.className = 'accordion';

        const summary = document.createElement('summary');
        summary.className = 'accordion__summary';
        summary.innerHTML = heading.innerHTML + ACCORDION_CHEVRON;

        const body = document.createElement('div');
        body.className = 'accordion__body rte';

        let next = heading.nextElementSibling;
        while (next && next.tagName !== 'H2') {
          const sibling = next;
          next = next.nextElementSibling;
          body.appendChild(sibling);
        }

        details.append(summary, body);
        heading.replaceWith(details);
      });
    });
  }

  /* --- Rastreio manual por código ------------------------------------------- */

  /**
   * A loja não tem API própria de rastreio — o link público dos Correios
   * (sem chave, sem backend) já resolve a maioria dos códigos usados nos
   * envios. Pedidos com conta na loja têm o link certo direto da
   * transportadora (fulfillment.tracking_url, vindo do próprio Shopify),
   * então esse formulário é só para quem só tem o código em mãos.
   */
  function initTrackingForm(root) {
    const form = root.querySelector('[data-tracking-form]');
    if (!form || form.dataset.trackingReady) return;
    form.dataset.trackingReady = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector('[data-tracking-input]');
      const code = (input.value || '').trim();
      if (!code) return;
      window.open(`https://rastreamento.correios.com.br/app/index.php?objetos=${encodeURIComponent(code)}`, '_blank', 'noopener');
    });
  }

  /* --- Detalhe do pedido (Minha Conta) -------------------------------------- */

  /**
   * O detalhe de cada pedido do cliente vem todo renderizado (escondido) na
   * página, porque Liquid não lê query string arbitrária no servidor — só o
   * ?view= do modelo, tratado à parte pela própria Shopify. `customer.orders`
   * já vem filtrado pela Shopify (nenhum risco de um cliente ver pedido de
   * outro); aqui só decide qual dos blocos já renderizados mostrar.
   */
  function initOrderDetail(root) {
    const wrap = root.querySelector('[data-order-detail-list]');
    if (!wrap || wrap.dataset.orderDetailReady) return;
    wrap.dataset.orderDetailReady = 'true';

    const requested = new URLSearchParams(window.location.search).get('order');
    const items = Array.from(wrap.querySelectorAll('[data-order-number]'));
    const match = items.find((el) => el.dataset.orderNumber === requested) || (!requested && items[0]);

    items.forEach((el) => {
      el.hidden = el !== match;
    });

    const empty = wrap.querySelector('[data-order-empty]');
    if (empty) empty.hidden = !!match;
  }

  /* --- Cupom de desconto no carrinho ----------------------------------------- */

  /**
   * /discount/{código}?redirect=... é o mecanismo nativo da Shopify pra
   * aplicar um desconto na sessão do cliente sem precisar de app — só marca
   * um cookie e recarrega a página de volta já com o valor refletido no
   * carrinho (cart.cart_level_discount_applications).
   */
  function initCouponForm(root) {
    const form = root.querySelector('[data-coupon-form]');
    if (!form || form.dataset.couponReady) return;
    form.dataset.couponReady = 'true';

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const input = form.querySelector('[data-coupon-input]');
      const code = (input.value || '').trim();
      if (!code) return;
      const redirect = encodeURIComponent(window.location.pathname);
      window.location.href = `${T.routes.root}discount/${encodeURIComponent(code)}?redirect=${redirect}`;
    });
  }

  /* --- Barra fixa de compra ------------------------------------------------ */

  function initStickyAtc() {
    const bar = document.querySelector('[data-sticky-atc]');
    const anchor = document.querySelector('[data-sticky-atc-anchor]');
    if (!bar || !anchor) return;

    const observer = new IntersectionObserver(
      ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
      { threshold: 0 }
    );
    observer.observe(anchor);
  }

  /* --- Voltar ao topo ------------------------------------------------------ */

  function initBackToTop() {
    const btn = document.querySelector('[data-back-to-top]');
    if (!btn) return;
    const onScroll = debounce(() => btn.classList.toggle('is-visible', window.scrollY > 500), 100);
    window.addEventListener('scroll', onScroll, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
    onScroll();
  }

  /* --- Seletor de quantidade ----------------------------------------------- */

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-qty-change]');
    if (!btn) return;
    const wrap = btn.closest('[data-qty]');
    const input = wrap && wrap.querySelector('[data-qty-input]');
    if (!input) return;

    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    const next = Number(input.value || min) + Number(btn.dataset.qtyChange);
    input.value = Math.min(max, Math.max(min, next));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* --- Filtros da coleção ---------------------------------------------------- */

  // Abre e fecha o painel de filtros no mobile.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-facets-toggle]')) return;
    const panel = document.querySelector('[data-facets-panel]');
    if (!panel) return;
    const open = panel.classList.toggle('is-open');
    document.documentElement.classList.toggle('is-locked', open);
  });

  // Marcar um filtro ou trocar a ordenação recarrega a listagem. O formulário é
  // um GET de verdade, então isso continua funcionando sem JavaScript.
  document.addEventListener('change', (event) => {
    const form = event.target.closest('[data-facets]');
    const sort = event.target.closest('[data-sort]');
    if (!form && !sort) return;
    const target = form || document.getElementById(sort.getAttribute('form'));
    if (target) target.submit();
  });

  /* --- Validação de formulário --------------------------------------------- */

  function initValidatedForm(form) {
    form.setAttribute('novalidate', '');

    function fieldError(input) {
      const id = input.getAttribute('aria-describedby');
      return id ? document.getElementById(id) : null;
    }

    function validate(input) {
      const valid = input.checkValidity();
      const error = fieldError(input);
      input.classList.toggle('input--error', !valid);
      input.setAttribute('aria-invalid', String(!valid));
      if (error) error.textContent = valid ? '' : input.dataset.errorMessage || '';
      return valid;
    }

    form.querySelectorAll('input, textarea').forEach((input) => {
      input.addEventListener('blur', () => validate(input));
      input.addEventListener('input', () => {
        if (input.classList.contains('input--error')) validate(input);
      });
    });

    form.addEventListener('submit', (event) => {
      const inputs = Array.from(form.querySelectorAll('input, textarea'));
      const invalid = inputs.filter((input) => !validate(input));
      if (invalid.length) {
        event.preventDefault();
        invalid[0].focus();
      }
    });
  }

  /* --- Inicialização ------------------------------------------------------- */

  function init(scope) {
    const root = scope || document;
    root.querySelectorAll('[data-carousel]').forEach(initCarousel);
    root.querySelectorAll('[data-hero-slider]').forEach(initHeroSlider);
    root.querySelectorAll('[data-tabs]').forEach(initTabs);
    root.querySelectorAll('[data-countdown]').forEach(initCountdown);
    root.querySelectorAll('[data-gallery]').forEach(initGallery);
    root.querySelectorAll('[data-validate]').forEach(initValidatedForm);
    initAutoAccordion(root);
    initTrackingForm(root);
    initOrderDetail(root);
    initCouponForm(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    init(document);
    initImageZoom();
    initDeptDrawer();
    initStickyAtc();
    initBackToTop();
    initAccountModals();
  });

  // Reinicializa o que a Shopify recarregar no editor de temas.
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  /* --- API interna --------------------------------------------------------- */

  T.utils = { formatMoney, fetchJSON, debounce, toast, trapFocus, releaseFocus, init };
})();
